import Clarity from '@microsoft/clarity';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate } from 'react-router-dom';
import { CLARITY_PROJECT_ID } from '../../../project_config';
import { api } from '../../../utils/api';
import { LoginHelper } from '../../../utils/LoginHelper';
import { useAdminGate } from '../../../utils/useAdminGate';
import Breadcrumb from '../../common/breadcrumb/Breadcrumb';
import Footer from '../../common/footer/Footer';
import Header from '../../common/header/Header';
import DashboardSidebar from '../DashboardSidebar';

const emptyForm = { title: '', description: '', price: '', duration_days: '', usage_limit: '' };

export default function AdminSubscriptionPlans() {
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
	const [plans, setPlans] = useState([]);
	const [form, setForm] = useState(emptyForm);
	const [editingId, setEditingId] = useState(null);
	const [error, setError] = useState('');
	const [notice, setNotice] = useState('');
	const [loading, setLoading] = useState(false);
	const { ready, isAdmin } = useAdminGate();

	useEffect(() => {
		Clarity.init(CLARITY_PROJECT_ID);
		const resize = () => setIsDesktop(window.innerWidth > 768);
		window.addEventListener('resize', resize);
		if (isAdmin) loadPlans();
		return () => window.removeEventListener('resize', resize);
	}, [isAdmin]);

	function loadPlans() {
		api.subscriptionAdminList({ token: LoginHelper.getToken() })
			.then(setPlans)
			.catch((err) => setError(err?.data?.message || 'دریافت پلن‌ها ناموفق بود'));
	}

	function editPlan(plan) {
		setEditingId(plan.id);
		setForm({ title: plan.title, description: plan.description || '', price: String(plan.price / 10), duration_days: String(plan.duration_days), usage_limit: String(plan.usage_limit) });
		setNotice('');
		setError('');
	}

	function resetForm() {
		setEditingId(null);
		setForm(emptyForm);
	}

	function savePlan(event) {
		event.preventDefault();
		setLoading(true);
		setError('');
		setNotice('');
		api.subscriptionSave({
			token: LoginHelper.getToken(),
			...(editingId ? { plan_id: editingId } : {}),
			title: form.title,
			description: form.description,
			price: Math.round(Number(form.price) * 10),
			duration_days: Number(form.duration_days),
			usage_limit: Number(form.usage_limit),
		}).then(() => {
			setNotice(editingId ? 'پلن ویرایش شد' : 'پلن ساخته شد');
			resetForm();
			loadPlans();
		}).catch((err) => setError(err?.data?.message || 'ذخیره پلن ناموفق بود'))
			.finally(() => setLoading(false));
	}

	function togglePlan(plan) {
		api.subscriptionToggleActive({ token: LoginHelper.getToken(), plan_id: plan.id })
			.then(() => loadPlans())
			.catch((err) => setError(err?.data?.message || 'تغییر وضعیت ناموفق بود'));
	}

	if (!ready) return null;
	if (!isAdmin) return <Navigate to="/login/" replace />;

	return <>
		<Helmet><title>مدیریت پلن‌های اشتراک | همراهتیم</title></Helmet>
		<div className="bg-[#F0F6FC] min-h-screen flex flex-col justify-between">
			<div>
				<Header isDesktop={isDesktop} />
				<div className="bg-white"><div className="max-w-[1200px] mx-auto px-4"><Breadcrumb linkDataList={[{ label: 'خانه', href: '/' }, { label: 'داشبورد مدیریت', href: '/dashboard/' }, { label: 'مدیریت اشتراک‌ها', href: '/dashboard/subscriptions/' }]} /></div></div>
				<main className={`${isDesktop ? 'max-w-[1200px] flex-row px-4' : 'p-[10px] flex-col'} w-full mx-auto flex gap-5 py-8`}>
					<DashboardSidebar isDesktop={isDesktop} />
					<section className="flex-1 bg-white border border-[#E2E8F0] rounded-[10px] p-5">
						<h1 className="font-Vazir-800 text-[24px] text-darkBlue">مدیریت پلن‌های اشتراک</h1>
						<form onSubmit={savePlan} className="grid grid-cols-2 sm:grid-cols-1 gap-3 mt-5">
							<input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="نام پلن" className="border rounded-lg p-3 font-Vazir-400" />
							<input required min="0" type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="قیمت (تومان)" className="border rounded-lg p-3 font-Vazir-400" />
							<input required min="1" type="number" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: e.target.value })} placeholder="مدت اعتبار (روز)" className="border rounded-lg p-3 font-Vazir-400" />
							<input required min="1" type="number" value={form.usage_limit} onChange={e => setForm({ ...form, usage_limit: e.target.value })} placeholder="محدودیت استفاده" className="border rounded-lg p-3 font-Vazir-400" />
							<textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="توضیحات (اختیاری)" className="col-span-2 sm:col-span-1 border rounded-lg p-3 font-Vazir-400" rows="3" />
							<div className="col-span-2 sm:col-span-1 flex gap-2">
								<button disabled={loading} className="rounded-lg bg-lightCta px-5 py-3 font-Vazir-700 text-darkBlue">{loading ? 'در حال ذخیره...' : editingId ? 'ذخیره تغییرات' : 'ساخت پلن'}</button>
								{editingId && <button type="button" onClick={resetForm} className="rounded-lg border px-5 py-3 font-Vazir-600">انصراف</button>}
							</div>
						</form>
						{notice && <p className="text-green-700 font-Vazir-400 mt-3">{notice}</p>}
						{error && <p className="text-red-600 font-Vazir-400 mt-3">{error}</p>}
						<div className="mt-6 grid gap-3">
							{plans.map(plan => <article key={plan.id} className="border border-[#E2E8F0] rounded-xl p-4 flex flex-wrap justify-between items-center gap-3">
								<div><h2 className="font-Vazir-700 text-darkBlue">{plan.title} {!plan.is_active && <span className="text-red-600 text-sm">(غیرفعال)</span>}</h2><p className="font-Vazir-400 text-midGray mt-1">{Number(plan.price / 10).toLocaleString('fa-IR')} تومان · {plan.duration_days} روز · {plan.usage_limit} استفاده</p>{plan.description && <p className="font-Vazir-400 text-midGray mt-1">{plan.description}</p>}</div>
								<div className="flex gap-2"><button onClick={() => editPlan(plan)} className="rounded-lg bg-lightCta px-4 py-2 font-Vazir-600">ویرایش</button><button onClick={() => togglePlan(plan)} className="rounded-lg border px-4 py-2 font-Vazir-600">{plan.is_active ? 'غیرفعال‌سازی' : 'فعال‌سازی'}</button></div>
							</article>)}
							{plans.length === 0 && <p className="text-center py-6 font-Vazir-400 text-midGray">هنوز پلنی ساخته نشده است.</p>}
						</div>
					</section>
				</main>
			</div>
			<Footer isDesktop={isDesktop} />
		</div>
	</>;
}
